import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUpdatedState} from 'helpers/hooks/utils';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import Delete from '@mui/icons-material/Delete';
import IconField from 'components/molecules/Fields/IconField/IconField';
import IconButton from 'components/atoms/Buttons/IconButton/IconButton';
import Icon from 'components/atoms/Icons/Icon/Icon';
import StyledListMappingForm from 'components/organisms/Forms/ListMappingForm/ListMappingForm.styles';
import FieldMappingsForm from 'components/organisms/Forms/FieldMappingsForm/FieldMappingsForm';

const ListMappingForm = React.forwardRef((props, ref) => {
  const {
    list,
    only,
    team,
    mappedFields,
    connection,
    fieldData,
    required,
    readOnly,
    disabled,
    removable,
    hideActions,
    error,
    errorFrom,
    errorTo,
    onChange,
    onError,
    onDelete,
    ...innerProps
  } = useComponentProps({
    elevation: 1,
    radius: 'round',
    ...props
  }, 'ListMappingForm');

  const formRef = useRef(null);
  const fieldsRef = useRef(null);
  const innerRef = useRef(null);

  const listMappingForm = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef
    },
    submit: (touch = true) => {
      return formRef.current?.submit(touch);
    },
    validate: (touch = true) => {
      return formRef.current?.validate(touch);
    },
    reset: (state) => {
      fieldsRef.current?.reset(state);
      setIsDirty(false);
      setInternalState({});
      return formRef.current?.reset(state);
    }
  }), []);

  useImperativeHandle(ref, () => listMappingForm);

  const [isDirty, setIsDirty] = useState(false);
  const [internalState, setInternalState] = useState(list);
  const [internalValue] = useUpdatedState(list);

  const isNew = !utils.isDefined(internalValue.from?.id) || !utils.isDefined(internalValue.to?.id);

  useEffect(() => {
    if (required && error) {
      return utils.observeTimeout(() => {
        try {
          formRef.current?.validate(true);
        } catch (e) {
          /* SQUASH */
        }
      }, constants.debounce.minimal)
    }
  }, [required, error]);

  const fields = useMemo(() => {
    const fields = [];

    const list = internalState ?? internalValue;
    const connectionDef = constants.data.lookup('connections', connection?.type);
    const directions = constants.data.mappingDirections
      .filter((md) => md.connections.find((c) => c === connection?.type))
      .filter((md) => !internalValue.to || !internalValue.to.readOnly || md.value === constants.client.connection.mappingDirections.in)
      .filter((md) => !internalValue.from || !internalValue.from.readOnly || md.value === constants.client.connection.mappingDirections.out);

    fields.push({
      name: 'from',
      label: `${constants.basics.shortName} list`,
      placeholder: `Type a ${constants.basics.shortName} list name`,
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.listObject,
      validate: (value, testContext) => {
        const values = testContext?.from?.[0]?.value || {};
        if (value && value?.readOnly && values.to && values.to.readOnly) {
          return testContext.createError({message: 'Select a not read only list'});
        } else {
          return true;
        }
      },
      formGroup: 'header',
      context: {
        teamId: team?.from?.id
      },
      options: 'internalLists',
      initial: internalValue.from,
      readOnly: readOnly,
      required: required || utils.isDefined(internalState.to?.id) || isDirty,
      disabled: disabled,
      FormFieldProps: {
        error: errorFrom ? 'Duplicate list' : null,
        variant: 'inlineLabel',
        size: 'smaller',
        hiddenLabel: true
      }
    });

    fields.push({
      name: 'direction',
      label: 'Direction',
      formGroup: 'header',
      type: constants.formFieldTypes.popper,
      validation: constants.formFieldValidationTypes.text,
      conversion: constants.formFieldConversionTypes.value,
      initial: internalValue?.direction ? internalValue?.direction :
        constants.data.mappingDirections.filter((md) => md.connections.find((c) => c === connection?.type))[0]?.value,
      readOnly: readOnly,
      required: true,
      disabled: disabled || directions.length === 1,
      FormFieldProps: {
        hiddenLabel: true,
        variant: 'inlineLabel',
        size: 'smallest',
        fullWidth: true,
        FormFieldComponent: IconField,
        FormFieldProps: {
          iconSet: directions,
          orientation: 'vertical',
          columns: 1,
          rows: 3,
          minWidth: 0,
          IconButtonProps: {
            variant: 'outlined',
            size: 'smaller',
            density: 'normal',
            color: 'text.primary'
          },
          IconProps: {
            size: 'smaller',
            color: 'text.primary'
          }
        },
        PopperProps: {
          ContextPopperProps: {
            density: 'dense',
            size: 'medium',
            placement: 'bottom',
            width: 50
          }
        },
        renderButton: (value, {ref, className, readOnly, disabled, onClick}) => {
          const directionDef = constants.data.lookup('mappingDirections', (value?.value ?? value));
          if (readOnly) {
            return <Icon size="smaller"
                         icon={directionDef?.icon}
                         color="secondary"
                         showTooltip={false}/>
          } else {
            return <IconButton ref={ref}
                               className={className}
                               variant="outlined"
                               size="smaller"
                               density="sparse"
                               color="secondary"
                               disabled={Boolean(disabled || readOnly)}
                               onClick={onClick}>
              <Icon size="smaller"
                    icon={directionDef?.icon}
                    showTooltip={false}/>
            </IconButton>
          }
        }
      }
    });

    fields.push({
      name: 'to',
      label: `${connectionDef.label} list`,
      placeholder: `Type ${connectionDef.vowelLabel ? 'an' : 'a'} ${connectionDef.label} list name`,
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.listObject,
      validate: (value, testContext) => {
        const values = testContext?.from?.[0]?.value || {};
        if (value && value?.readOnly && values.from && values.from.readOnly) {
          return testContext.createError({message: 'Select a not read only list'});
        } else {
          return true;
        }
      },
      formGroup: 'header',
      context: {
        type: connection?.type,
        key: connection?.key,
        teamId: team?.to?.id
      },
      options: 'externalLists',
      initial: internalValue.to,
      readOnly: readOnly,
      required: required || utils.isDefined(internalState.to?.id),
      disabled: disabled,
      FormFieldProps: {
        error: errorTo ? 'Duplicate list' : null,
        variant: 'inlineLabel',
        size: 'smaller',
        hiddenLabel: true
      }
    });

    const mappedListFields = mappedFields
      .filter((mf) => (mf.list?.from?.id || 0) !== list?.from?.id || (mf.list?.to?.id || 0) !== list?.to?.id);

    fields.push({
      name: 'fields',
      label: 'Fields',
      type: constants.formFieldTypes.component,
      validation: `${constants.formFieldValidationTypes.component}(${constants.formFieldValidationTypes.unique}(${constants.formFieldValidationTypes.fieldMapping}))`,
      conversion: constants.formFieldConversionTypes.component,
      entity: 'mapping',
      valueProp: 'fields',
      formGroup: 'body',
      Component: <FieldMappingsForm list={list}
                                    team={team}
                                    mappedFields={mappedListFields}
                                    connection={connection}
                                    fieldData={fieldData} />,
      initial: {
        value: internalValue.fields ?? [],
        errors: false
      },
      required: false,
      readOnly: readOnly,
      disabled: disabled || !utils.isDefined(list.from?.id) || !utils.isDefined(list.to?.id),
      FormFieldProps: {
        ref: fieldsRef,
        variant: 'staticLabel',
        hiddenLabel: false,
        autoFocus: false
      }
    });

    return fields;
  }, [internalValue, internalState, connection, team, mappedFields, fieldData,
    errorFrom, errorTo, readOnly, disabled, required, isDirty]);

  const onChangeEvent = useEffectEvent(onChange);
  const handleSubmit = useCallback((values, actions) => {
    const directions = constants.data.mappingDirections
      .filter((md) => md.connections.find((c) => c === connection?.type));

    if (values['from'] && values['from'].readOnly) {
      if (directions.find((md) => md.value === constants.client.connection.mappingDirections.out)) {
        values['direction'] = constants.client.connection.mappingDirections.out;
      }
    } else if (values['to'] && values['to'].readOnly) {
      if (directions.find((md) => md.value === constants.client.connection.mappingDirections.in)) {
        values['direction'] = constants.client.connection.mappingDirections.in;
      }
    }

    utils.asPromise(onChangeEvent)({...values})
      .finally(() => {
        actions.setSubmitting(false);
      });
  }, [connection?.type, onChangeEvent]);

  const handleChange = useCallback(() => {
    formRef.current?.submit(false);
  }, []);

  const handleError = useCallback((field, value) => {
    utils.asPromise(onChangeEvent)({...internalValue, [field.name]: value});
  }, [internalValue, onChangeEvent]);

  const handleChangeDirect = useCallback((e) => {
    const field = fields?.find((field) => field.name === e?.target?.name);
    const value = e?.target?.value;

    const resetFields = () => {
      fieldsRef.current?.setValue?.({value: [], errors: false});
      fieldsRef.current?.reset?.({touched: {}});
      formRef.current?.touch?.({fields: false});
    }

    if (field) {
      if (field.name === 'from') {
        setInternalState((current) => {
          if (current.from?.id !== value?.id) {
            resetFields();
            return {
              ...current,
              from: value
            }
          } else {
            return current;
          }
        });
      } else if (field.name === 'to') {
        setInternalState((current) => {
          if (current.to?.id !== value?.id) {
            resetFields();
            return {
              ...current,
              to: value
            }
          } else {
            return current;
          }
        });
      }
    }
  }, [fields]);

  const onErrorEvent = useEffectEvent(onError);
  const handleValidating = useCallback((validating, dirty, errors) => {
    setIsDirty((current) => current || dirty || errors);
    onErrorEvent?.(errors);
  }, [onErrorEvent]);

  const onDeleteEvent = useEffectEvent(onDelete);
  const deleteAction = useMemo(() => ({
    label: 'Remove list',
    tooltip: 'Remove list',
    icon: Delete,
    ActionIconButtonProps: {
      IconProps: {
        size: 'smaller'
      }
    },
    IconButtonProps: {
      disabled: (disabled || readOnly || !removable || (only && isNew && !isDirty))
    },
    onClick: (e) => {
      onDeleteEvent?.(e);
    }
  }), [disabled, readOnly, removable, only, isDirty, isNew, onDeleteEvent]);

  const renderFormContent = useCallback((groups) => {
    const header = groups.find((g) => g.name === 'header');
    const body = groups.find((g) => g.name === 'body');

    const renderActions = () => {
      return <Box className="ListMappingForm-actions">
        <ActionIconButton size="smaller"
                          density="sparse"
                          variant="contained"
                          color="error"
                          action={deleteAction}/>
      </Box>
    }

    return <React.Fragment>
      <Box className="ListMappingForm-header">
        <Box className="Form-fields">
          {header.rendered}
        </Box>
        {!hideActions ? renderActions() : null}
      </Box>
      {body ? <Box className="ListMappingForm-body">
        <Box className="Form-fields">
          {body.rendered}
        </Box>
      </Box> : null}
    </React.Fragment>
  }, [deleteAction, hideActions]);

  return <StyledListMappingForm ref={innerRef} {...innerProps}>
    <InlineForm ref={formRef}
                className="ListMappingForm-form"
                fieldData={fieldData}
                autoFocus={isNew && !only}
                onSubmit={handleSubmit}
                onChange={handleChange}
                onError={handleError}
                onChangeDirect={handleChangeDirect}
                onValidating={handleValidating}
                renderContent={renderFormContent}
                fields={fields}/>
  </StyledListMappingForm>
});

ListMappingForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  list: PropTypes.object,
  only: PropTypes.bool,
  team: PropTypes.object,
  mappedFields: PropTypes.array,
  connection: PropTypes.object,
  fieldData: PropTypes.object,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  removable: PropTypes.bool,
  hideActions: PropTypes.bool,
  error: PropTypes.bool,
  errorFrom: PropTypes.bool,
  errorTo: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func
};

export default ListMappingForm;
